import { ComboboxItem } from '@mantine/core';
import { BenefitStatus, OnboardingPeriodReportDto } from '@zorro/clients';
import { Select } from '@zorro/design-system/v2/Select';
import {
  formatDateEnLocaleRange,
  parseDateISO,
} from '@zorro/shared/formatters';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { useMemo } from 'react';

type EmployeeBenefitData = {
  effectiveFrom: string;
  effectiveUntil: string;
  benefitId: string;
  periodId: string;
};

const mapOnboardingPeriodsToSelectOptions = (
  onboardingPeriods: OnboardingPeriodReportDto[],
  benefitsData: EmployeeBenefitData[],
  onboardingPeriodsFilter: (
    onboardingPeriod: OnboardingPeriodReportDto
  ) => boolean
): ComboboxItem[] => {
  return onboardingPeriods
    .filter((value) => onboardingPeriodsFilter(value))
    .sort((first, second) =>
      parseDateISO(first.onboardingFrom).isAfter(
        parseDateISO(second.onboardingFrom),
        'day'
      )
        ? -1
        : 1
    )
    .map(
      ({
        id,
        isActive,
        isSpecialEnrollment,
        effectiveFrom,
        effectiveUntil,
        enrollmentEffectiveFrom,
      }) => {
        const opBenefit = benefitsData.find(({ periodId }) => id === periodId);

        let label: string;
        if (opBenefit) {
          label = formatDateEnLocaleRange(effectiveFrom, effectiveUntil);
        } else if (isActive) {
          label = 'Upcoming enrollment';
        } else if (isSpecialEnrollment) {
          label = `Special enrollment ${parseDateISO(
            enrollmentEffectiveFrom
          ).year()}`;
        } else {
          label = `Open enrollment ${parseDateISO(
            enrollmentEffectiveFrom
          ).year()}`;
        }

        return {
          value: id,
          label,
        };
      }
    );
};

type Props = {
  employeeId: string;
  onSelectedPeriodChange: (newSelectedPeriodId: string) => void;
  selectedPeriodId?: string;
  onboardingPeriodFilter?: (
    onboardingPeriod: OnboardingPeriodReportDto
  ) => boolean;
};

export const OnboardingPeriodSelect = ({
  employeeId,
  onSelectedPeriodChange,
  selectedPeriodId,
  onboardingPeriodFilter = () => true,
}: Props) => {
  const { isLoading, data: onboardingPeriodsReport = [] } = useMonolithQuery({
    method: 'onboardingPeriodReportsControllerGenerateEmployeeOnboardingReport',
    params: [employeeId],
  });

  const options = useMemo(() => {
    const benefitsData = onboardingPeriodsReport.flatMap(
      (onboardingPeriodReport) =>
        onboardingPeriodReport.majorMedicalBenefit?.status &&
        onboardingPeriodReport.majorMedicalBenefit.status !==
          BenefitStatus.IN_CART
          ? [
              {
                effectiveFrom: onboardingPeriodReport.effectiveFrom,
                effectiveUntil: onboardingPeriodReport.effectiveUntil,
                benefitId: onboardingPeriodReport.majorMedicalBenefit.id,
                periodId: onboardingPeriodReport.id,
              },
            ]
          : []
    );
    return mapOnboardingPeriodsToSelectOptions(
      onboardingPeriodsReport,
      benefitsData,
      onboardingPeriodFilter
    );
  }, [onboardingPeriodsReport, onboardingPeriodFilter]);

  const isNotReady = isLoading || (!selectedPeriodId && options.length > 0);
  const isNoOptions = options.length <= 1;

  if (isNotReady || isNoOptions) {
    return null;
  }

  const value = options.find(({ value }) => selectedPeriodId === value)?.value;

  return (
    <Select
      data={options}
      value={value}
      onChange={(newSelectedPeriodId) => {
        if (newSelectedPeriodId) {
          onSelectedPeriodChange(newSelectedPeriodId);
        }
      }}
      size="xs"
      allowDeselect={false}
    />
  );
};
